import ApiError from './ApiError';
import ApiService, { type QueryParam, type RequestArgs, type ResponseCode } from './ApiService';

const ACTION_METHOD_MAP = {
  GET: 'read',
  PUT: 'update',
  POST: 'create',
  POST_V1: null,
  DELETE: 'delete',
} as const;

type ApiResponse<T, U extends string> = {
  result: ResponseCode;
  time: number;
  token: string;
} & { [key in U]: { data: T } };

/**
 * Class Adapter based on ApiService to make api operations on Ubiwan's APIs v1.
 * @extends ApiService
 */
class ApiV1Adapter<T, U extends QueryParam = {}> extends ApiService<T, U> {
  public polling_timestamp!: number;
  /**
   * Return only the main data from api.
   *
   * Due to no convention on API v1, there is multiple way to return the data.
   *
   * @param response - The whole response from api.
   * @returns The main data
   */
  private formatResponse(response: ApiResponse<T, typeof this.entity | 'read'>) {
    return response[this.entity]?.data || response.read?.data || response[this.entity] || response.create;
  }

  /**
   * Core request to the API using fetch.
   *
   * API v1 mainly accept GET method and body
   * and query params is filled into query string.
   *
   * @param args
   * @returns - Response of API
   * @throws {ApiError}
   */
  //@ts-ignore
  public async request(args: RequestArgs<T, U>) {
    this.token = localStorage.getItem('token');

    let queryString: string = '?';
    let formBodyData: FormData | null = null;

    // Set queryString according to token , method, queryParams and params
    if (this.token) queryString = `?token=${this.token}`;
    if (ACTION_METHOD_MAP[args.method]) queryString += `&action=${ACTION_METHOD_MAP[args.method]}`;
    if (args.queryParams) queryString += `&${ApiService.toQueryString(args.queryParams)}`;
    if (args.params) queryString += `&${ApiService.toQueryString(args.params)}`;

    // Concat url
    let url = `${this.baseUrl}/${this.entity}${queryString}`;

    // Set body according to method
    if (args.method === 'POST_V1' && !!args.body) {
      //@ts-ignore
      delete args.body?.timestamp; // temporary quick fix to not erase timestamp on api side.
      formBodyData = new FormData();
      Object.keys(args.body).forEach(key => {
        formBodyData?.append(key, args.body[key]);
      });
    } else if (args.body) {
      //@ts-ignore
      delete args.body?.timestamp; // temporary quick fix to not erase timestamp on api side.
      const bodyString = `&${ApiService.toQueryString(args.body)}`;
      url += bodyString;
    }

    // Make the request
    const response = await fetch(url, {
      method: args.method === 'POST_V1' ? 'POST' : 'GET',
      body: formBodyData,
    });

    const jsonResponse: ApiResponse<T, typeof this.entity> = await response.json();

    this.polling_timestamp = jsonResponse.time;

    if (
      jsonResponse.result !== 200 &&
      jsonResponse.result !== 201 &&
      !(this.entity === 'tokens' && jsonResponse.result === 1064) // TODO: remove this condition when API v1 will be fixed (dumb sql request)
    )
      throw jsonResponse.result;

    return this.formatResponse(jsonResponse);
  }

  /**
   * Bypass request method to make a real POST operation.
   *
   * Since Ubinwan's API v1 no respect REST convention
   * it is necessary to create custom post to make a real
   * post because request of the class make only GET operation.
   *
   * @param body - body of the request
   * @returns The result of the post request
   */
  public async customPost(body: T) {
    return this.request({ method: 'POST_V1', body });
  }
}

export default ApiV1Adapter;
